//
// ChatAudioRecording
//
// Audio recording message component for chat interface
// Handles playback of audio messages with visual feedback
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useState,
  useCallback,
  useEffect,
  useRef,
  memo,
  lazy,
  Suspense,
} from 'react';

import {
  decodeAudioBlob,
  logError,
} from '../../../../utils';
import secondsToTime from '../../../../utils/dateTime/secondsToTime';
import { Button } from '../../../buttons';
import {
  Column,
  Row,
} from '../../../display';
import { useIsMounted } from '../../../React';
import ChatAudioRecorderTicks from '../ChatAudioRecorderTicks';

import ChatMessageControls from './ChatMessageControls';
import {
  ChatAuthorIcon,
  ChatAuthorName,
  ChatDate,
  ChatItemClick,
  MessageFrame,
} from './components';

// Lazy load OgvAudioPlayer - only downloaded when Safari needs to play webm
const OgvAudioPlayer = lazy(() => import('../OgvAudioPlayer'));

const audioTicks = 20; // Same as recorder

const ChatAudioRecording = memo(({
  registerChild,
  translate,
  style,
  message,
  author,
  initials,
  crBlocked,
  color,
  gcatCode,
  csid,
  piid,
  getMedia,
  audioAvailable,
  audioStartContext,
  audioContextRef,
  audioAnalyzerRef,
  visibleInThisGathering = true,
  grpGpid,
  grpLbl,
  grpNbr,
  groupsMap,
  imagesMap,
  prtnrLogoUrl,
  selectGathering,
}) => {
  const [playing, setPlaying] = useState(false);
  const [pending, setPending] = useState(false);
  const [audioBlob, setAudioBlob] = useState(false);
  // Decoded audioArray (null if using ogvPlayer):
  const [audioBuffer, setAudioBuffer] = useState();
  // AudioBufferSourceNode set during playing (native playback):
  const [playbackSourceNode, setPlaybackSourceNode] = useState(false);
  // Flag indicating ogv.js is needed for playback (Safari + webm):
  const [needsOgv, setNeedsOgv] = useState(false);
  const ogvPlayerRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [startTime, setStartTime] = useState();
  const [playTime, setPlayTime] = useState();
  const [audioLevels, setAudioLevels] = useState([]);
  const enableAnimateAudioRef = useRef(false);
  const lastAnimateAudioRef = useRef(false);
  const isMountedRef = useIsMounted();
  const {
    id,
    gthrId,
    msgRdatim1,
    msgId,
    fileInfo,
  } = message;
  const { durationTS } = fileInfo;

  const handlePlayClick = useCallback(
    () => {
      if (audioAvailable && audioStartContext) {
        audioStartContext();

        if (csid && (!playing)) {
          if (!audioBlob) {
            getMedia(
              {
                csid, piid, msgId, type: 'blob',
              },
              (err, newAudioBlob) => {
                if (isMountedRef.current) {
                  if (err) {
                    setPlaying(false);
                    setPending(false);
                  } else {
                    setAudioBlob(newAudioBlob);
                  }
                }
              },
            );
            setPending(true);
          }
          setPlaying(true);
        } else {
          setPlaying(false);
        }
      }
    },
    [
      playing, audioBlob, msgId, csid, getMedia, isMountedRef,
      audioAvailable, audioStartContext, piid,
    ],
  );

  // Generate audio levels from buffer for visualization
  const generateAudioLevels = useCallback(
    (buffer) => {
      const bufferChannelData = buffer.getChannelData(0);
      const samplesPerSegment = Math.floor(bufferChannelData.length / audioTicks);
      const levels = [];

      for (let i = 0; i < audioTicks; i += 1) {
        const start = i * samplesPerSegment;
        const end = Math.min(start + samplesPerSegment, bufferChannelData.length);

        let rms = 0;
        let count = 0;
        for (let j = start; j < end; j += 1) {
          rms += bufferChannelData[j] * bufferChannelData[j];
          count += 1;
        }

        rms = Math.sqrt(rms / count);

        let level = Math.min(100, Math.max(0, Math.log10(rms * 100 + 1) * 50));

        if (rms > 0.001) {
          level = Math.max(level, 15);
        }

        levels.push(Math.floor(level));
      }

      setAudioLevels(levels);
    },
    [],
  );

  // Decode audio blob (with ogv.js fallback for Safari + webm)
  useEffect(
    () => {
      if (audioBlob && audioContextRef.current) {
        // Create blob URL for potential ogv player use
        const newBlobUrl = URL.createObjectURL(audioBlob);
        setBlobUrl(newBlobUrl);

        decodeAudioBlob(audioBlob, audioContextRef.current)
          .then((result) => {
            if (!isMountedRef.current) return;

            if (result.needsOgvPlayer) {
              // Safari + webm: use lazy-loaded OgvAudioPlayer
              setNeedsOgv(true);
              // Generate placeholder audio levels (flat bars since we can't decode)
              setAudioLevels(Array(audioTicks).fill(30));
            } else {
              // Native playback: use AudioBuffer
              const newAudioBuffer = result.audioBuffer;

              // Apply fade-in/out like in recorder
              const fadeInSamples = Math.min(512, newAudioBuffer.length);
              const channelData = newAudioBuffer.getChannelData(0);

              for (let i = 0; i < fadeInSamples; i += 1) {
                const fadeMultiplier = i / fadeInSamples;
                channelData[i] *= fadeMultiplier;
              }

              const fadeOutSamples = Math.min(512, newAudioBuffer.length);
              const startFadeOut = newAudioBuffer.length - fadeOutSamples;

              for (let i = 0; i < fadeOutSamples; i += 1) {
                const fadeMultiplier = (fadeOutSamples - i) / fadeOutSamples;
                channelData[startFadeOut + i] *= fadeMultiplier;
              }

              setAudioBuffer(newAudioBuffer);
              generateAudioLevels(newAudioBuffer);
            }
          })
          .catch((error) => {
            logError(
              `Failed to decode audio: ${error.message}`,
              'ChatAudioRecording.decodeAudio.failed',
              {
                err: error?.message,
                blobType: audioBlob?.type,
                blobSize: audioBlob?.size,
              },
            );
          });

        // Cleanup blob URL on unmount
        return () => {
          URL.revokeObjectURL(newBlobUrl);
          setBlobUrl(null);
        };
      }
      return undefined;
    },
    [audioBlob, audioContextRef, isMountedRef, generateAudioLevels],
  );

  // Handle native AudioBuffer playback
  useEffect(
    () => {
      if (audioBuffer) {
        if (playing && (!playbackSourceNode)) {
          const newPlaybackSourceNode = audioContextRef.current.createBufferSource();
          const audioAnalyzer = audioAnalyzerRef.current;
          newPlaybackSourceNode.connect(audioAnalyzer);
          newPlaybackSourceNode.connect(audioContextRef.current.destination);
          newPlaybackSourceNode.buffer = audioBuffer;
          setPlaybackSourceNode(newPlaybackSourceNode);
          const startOffset = 0;
          newPlaybackSourceNode.start(0, startOffset);
          setPending(false);
          newPlaybackSourceNode.addEventListener(
            'ended',
            () => {
              setStartTime();
              setPlaybackSourceNode();
              setPlaying(false);
            },
          );
          setStartTime(audioContextRef.current.currentTime - startOffset);
        }
      }
      return undefined;
    },
    [audioBuffer, audioContextRef, playbackSourceNode, playing, audioAnalyzerRef],
  );

  // OGV player callbacks
  const handleOgvEnded = useCallback(
    () => {
      setStartTime();
      setPlaying(false);
    },
    [],
  );

  const handleOgvCanPlay = useCallback(
    () => {
      setPending(false);
    },
    [],
  );

  const handleOgvTimeUpdate = useCallback(
    (currentTime) => {
      setPlayTime(Math.floor(currentTime * 10) / 10);
    },
    [],
  );

  // Start ogv playback when playing is set
  useEffect(
    () => {
      if (needsOgv && playing && ogvPlayerRef.current) {
        setStartTime(Date.now() / 1000);
        ogvPlayerRef.current.play();
      } else if (needsOgv && (!playing) && ogvPlayerRef.current) {
        ogvPlayerRef.current.pause();
      }
    },
    [needsOgv, playing],
  );

  // Stop native playback
  useEffect(
    () => {
      if (playbackSourceNode && (!playing)) {
        playbackSourceNode.stop();
        setPlaybackSourceNode();
        setStartTime();
      }
    },
    [playbackSourceNode, playing],
  );

  const animateAudio = useCallback(
    () => {
      if (enableAnimateAudioRef.current) {
        let timeSpent;
        if (needsOgv && startTime) {
          // OGV player: use Date.now() based timing
          timeSpent = (Date.now() / 1000) - startTime;
        } else if (audioContextRef.current && startTime) {
          // Native playback: use audioContext time
          timeSpent = audioContextRef.current.currentTime - startTime;
        }

        if (timeSpent !== undefined) {
          setPlayTime(Math.floor(timeSpent * 10) / 10);
        }

        lastAnimateAudioRef.current = requestAnimationFrame(animateAudio);
      } else {
        setPlayTime();
      }
    },
    [audioContextRef, startTime, needsOgv],
  );

  useEffect(
    () => {
      if (lastAnimateAudioRef.current) {
        cancelAnimationFrame(lastAnimateAudioRef);
      }
      if (playbackSourceNode || (needsOgv && playing)) {
        if (enableAnimateAudioRef.current) {
          animateAudio();
        } else {
          enableAnimateAudioRef.current = true;
          animateAudio();
        }
      } else if (enableAnimateAudioRef.current) {
        enableAnimateAudioRef.current = false;
      }
    },
    [animateAudio, playbackSourceNode, needsOgv, playing],
  );

  // Calculate playback progress for visualization
  const audioDuration = durationTS / 10; // Convert from tenths of seconds
  const playProgress = playTime && audioDuration ? playTime / audioDuration : 0;

  return (
    <div
      ref={registerChild}
      key={id}
      className={classNames(
        'w-full py-2 gap-1 flex flex-col relative items-center justify-center overflow-hidden group',
        {
          'px-6': visibleInThisGathering,
          'px-2 opacity-50': !visibleInThisGathering,
        },
      )}
      style={style}
    >
      <ChatItemClick
        gthrId={gthrId}
        selectGathering={selectGathering}
        visibleInThisGathering={visibleInThisGathering}
      />

      {/* Lazy-loaded OGV player for Safari + webm */}
      {needsOgv && blobUrl && (
        <Suspense fallback={null}>
          <OgvAudioPlayer
            ref={ogvPlayerRef}
            blobUrl={blobUrl}
            onEnded={handleOgvEnded}
            onCanPlay={handleOgvCanPlay}
            onTimeUpdate={handleOgvTimeUpdate}
          />
        </Suspense>
      )}
      <ChatAuthorName
        translate={translate}
        author={author}
        initials={initials}
        crBlocked={crBlocked}
        gcatCode={gcatCode}
        grpNbr={grpNbr}
        prtnrLogoUrl={prtnrLogoUrl}
      />

      <Row
        start={!!author}
        end={!author}
        fullw
      >
        <ChatAuthorIcon
          author={author}
          initials={initials}
          color={color}
          gcatCode={gcatCode}
          grpGpid={grpGpid}
          grpLbl={grpLbl}
          groupsMap={groupsMap}
          imagesMap={imagesMap}
        />

        <MessageFrame
          key={id}
          author={author}
          visibleInThisGathering={visibleInThisGathering}
        >
          {(csid)
            ? (
                <Column gap={1} crossStart fullw className="py-1">
                  <span className="italic text-sm mb-2">
                    {`${translate('audioRecording', 'chat')}\u00A0:`}
                  </span>

                  <Row className="px-3 py-2 bg-white/30 rounded-lg gap-3 items-center">
                    <Button
                      type="button"
                      onClick={handlePlayClick}
                      disabled={pending}
                      imgSrc={playing
                        ? '/images/svg/gui/stop.svg'
                        : '/images/svg/gui/play.svg'}
                      noFrame
                      imgColorAsCssFill
                      className={classNames(
                        'mx-auto',
                        {
                          'fill-msgOthersTxt': author,
                          'fill-msgMineTxt': !author,
                        },
                      )}
                    />

                    <ChatAudioRecorderTicks
                      audioLevels={audioLevels}
                      isRecording={false}
                      isPlaying={playing}
                      playProgress={playProgress}
                      totalTicks={audioTicks}
                      messaging
                      author={author}
                      className="mx-auto"
                    />

                    <span className="text-sm font-medium shrink-0 min-w-12 text-center">
                      {secondsToTime(audioDuration)}
                    </span>
                  </Row>
                </Column>
              )
            : (
                <div className="w-full flex items-center pb-2">
                  <span>-- Missing case --</span>
                </div>
              )}
        </MessageFrame>

        <Column
          className={classNames(
            'self-start',
            {
              'order-2': !author,
              'order-4': author,
            },
          )}
          gap={1}
          crossStart={author}
          crossEnd={!author}
        >
          <ChatDate
            dtime={msgRdatim1}
            myMessage={!author}
          />

          <ChatMessageControls
            translate={translate}
            message={message}
            author={author}
          />
        </Column>

      </Row>
    </div>
  );
});
ChatAudioRecording.displayName = 'msgRdatim1';

ChatAudioRecording.propTypes = {
  registerChild: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  style: PropTypes.shape({}).isRequired,
  author: PropTypes.string,
  initials: PropTypes.string,
  crBlocked: PropTypes.number,
  color: PropTypes.string,
  gcatCode: PropTypes.string,
  csid: PropTypes.string,
  piid: PropTypes.string,
  getMedia: PropTypes.func.isRequired,
  audioAvailable: PropTypes.bool,
  audioStartContext: PropTypes.func,
  audioContextRef: PropTypes.shape({
    current: PropTypes.shape({
      decodeAudioData: PropTypes.func,
      createMediaStreamSource: PropTypes.func,
      createBufferSource: PropTypes.func,
      destination: PropTypes.shape({}),
      currentTime: PropTypes.number,
    }).isRequired,
  }).isRequired,
  audioAnalyzerRef: PropTypes.shape({
    current: PropTypes.shape({
      frequencyBinCount: PropTypes.number,
      getByteTimeDomainData: PropTypes.func,
    }).isRequired,
  }).isRequired,
  visibleInThisGathering: PropTypes.bool,
  grpGpid: PropTypes.string,
  grpLbl: PropTypes.string,
  grpNbr: PropTypes.number,
  groupsMap: PropTypes.instanceOf(Map).isRequired,
  imagesMap: PropTypes.instanceOf(Map).isRequired,
  selectGathering: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    gthrId: PropTypes.number,
    msgId: PropTypes.number.isRequired,
    msgRdatim1: PropTypes.string.isRequired,
    fileInfo: PropTypes.shape({
      durationTS: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  prtnrLogoUrl: PropTypes.string,
};

export default ChatAudioRecording;
